import { ReactNode, useEffect, useState } from "react";
import { NavigateFunction, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Description,
  Download,
  Login,
  Logout,
  PlayArrow,
  Podcasts,
  PublicOutlined,
  SwapHoriz,
  Tv,
  Warning,
} from "@mui/icons-material";
import {
  ArchiveDescription as PerCorpusDoc,
  ArchiveDoc,
  CrossSetDescription,
  describeArchive,
  getAllArchiveRemarks,
  getArchiveDescription,
  getArchiveRemarks,
  isCrossSet,
  Remark,
  RemarkCategory,
  StationRemark,
} from "../api/archive";
import { getInventory, InventoryRow } from "../api/inventory";
import { getMe, MeResponse } from "../api/me";
import { ApiError, extractErrorMessage } from "../api/client";
import { formatTs } from "../api/workflowSummary";
import { renderArchiveReport } from "../components/archive/archiveReportRender";
import { ChartSeries, TimelineBarChart } from "../components/archive/TimelineBarChart";
import { PRESIDENTIAL_TERMS } from "../components/archive/presidentialTerms";
import { WORLD_EVENTS } from "../components/archive/worldEvents";
import { DATASET_ERAS } from "../components/archive/eras";
import { YearCoverageTable } from "../components/archive/YearCoverageTable";

// Read-only view of the assembled archive description. Broadcast and
// podcast each get their own framing (podcast adds by-language + explicit);
// the "All" view is the cross-corpus rollup on the shared dimensions
// (source_type split, top sources, categories) — never summed apples-to-
// oranges beyond a document count.

const fmt = (n: number | null | undefined): string =>
  n === null || n === undefined ? "—" : n.toLocaleString();

const metric = (n: number | null | undefined): string => {
  if (n === null || n === undefined) return "—";
  return Math.abs(n) >= 1_000_000
    ? new Intl.NumberFormat(undefined, { notation: "compact", maximumFractionDigits: 1 }).format(n)
    : Math.round(n).toLocaleString();
};

const REMARK_PAGE = 50;

// Auto-detected station activity hidden until the signals are validated
// (re-key vs capture-flip, outage vs never-stable). The lists themselves now
// load lazily+paged from /archive/remarks regardless of this gate.
const SHOW_STATION_ACTIVITY = false;

const EMPTY_SUMMARY = { arrivals: 0, departures: 0, rekeys: 0, outages: 0, total: 0 };

// Cap how many rows we render in the ranked source/category lists. The doc
// can carry thousands (podcast alone has ~118k sources / many categories);
// rendering every row as a DOM node balloons the page and hangs the browser.
// Lists are already sorted descending, so a slice = the top N.
const MAX_LIST_ROWS = 30;

const CT_LABEL: Record<string, string> = {
  broadcast: "Broadcast",
  podcast: "Podcast",
  all: "All",
};

export function ArchiveDescription() {
  const navigate = useNavigate();
  const [contentType, setContentType] = useState<string>("all");
  const [doc, setDoc] = useState<ArchiveDoc | null>(null);
  const [inventory, setInventory] = useState<InventoryRow[] | null>(null);
  const [me, setMe] = useState<MeResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [describing, setDescribing] = useState(false);

  useEffect(() => {
    getInventory()
      .then((r) => setInventory(r.inventory))
      .catch(() => undefined);
    getMe()
      .then(setMe)
      .catch(() => undefined);
  }, []);

  // Reload the doc whenever the corpus switches.
  useEffect(() => {
    let cancelled = false;
    setDoc(null);
    setError(null);
    getArchiveDescription(contentType)
      .then((d) => !cancelled && setDoc(d))
      .catch(
        (err) =>
          !cancelled &&
          setError(`archive description load failed: ${extractErrorMessage(err)}`),
      );
    return () => {
      cancelled = true;
    };
  }, [contentType]);

  const isAdmin = me?.role === "admin";
  // Rollup-month count for the *current* corpus (drives the incomplete
  // warning). Not meaningful for the cross-set view.
  const rollupMonths =
    inventory === null
      ? null
      : inventory.filter(
          (m) => m.has_rollup === 1 && (m.content_type ?? "broadcast") === contentType,
        ).length;

  const describeStale = async () => {
    setError(null);
    setNotice(null);
    setDescribing(true);
    try {
      const r = await describeArchive({ stale: true, content_type: contentType });
      setNotice(
        r.count === 0
          ? "All months are already up to date — nothing to describe."
          : `Submitted ${r.count} describe job(s). They run on the operator; ` +
              "refresh in a bit to see the cache fill in.",
      );
    } catch (err) {
      setError(
        extractErrorMessage(
          err,
          err instanceof ApiError ? `describe returned ${err.status}` : undefined,
        ),
      );
    } finally {
      setDescribing(false);
    }
  };

  const switcher = (
    <ToggleButtonGroup
      exclusive
      size="small"
      color="primary"
      value={contentType}
      onChange={(_e, v) => v && setContentType(v)}
    >
      <ToggleButton value="broadcast">
        <Tv fontSize="small" sx={{ mr: 0.75 }} /> Broadcast
      </ToggleButton>
      <ToggleButton value="podcast">
        <Podcasts fontSize="small" sx={{ mr: 0.75 }} /> Podcast
      </ToggleButton>
      <ToggleButton value="all">
        <PublicOutlined fontSize="small" sx={{ mr: 0.75 }} /> All
      </ToggleButton>
    </ToggleButtonGroup>
  );

  const header = (subtitle: ReactNode, actions: ReactNode) => (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      mb={3}
      flexWrap="wrap"
      gap={2}
    >
      <Stack direction="row" alignItems="center" gap={2}>
        <Description fontSize="large" color="primary" />
        <Box>
          <Typography variant="h4">Archive description</Typography>
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        </Box>
      </Stack>
      <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
        {switcher}
        {actions}
      </Stack>
    </Stack>
  );

  if (error && !doc) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {header("", null)}
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }
  if (!doc) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {header(`Loading ${CT_LABEL[contentType] ?? contentType}…`, null)}
        <CircularProgress size={20} />
      </Container>
    );
  }

  const alerts = (
    <>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}
    </>
  );

  // Cross-set view: shared-dimension rollup across corpora.
  if (isCrossSet(doc)) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {header(
          `Whole archive · ${doc.corpora.map((c) => CT_LABEL[c] ?? c).join(" + ")} · shared dimensions`,
          null,
        )}
        {alerts}
        <CrossSetSections doc={doc} />
      </Container>
    );
  }

  const describeAction = (
    <Tooltip
      title={
        isAdmin
          ? "Enqueue a describe job for every month whose cached description is missing or out of date (one Job per month)."
          : "Admin only — describe jobs can only be started by an administrator."
      }
    >
      <span>
        <Button
          variant="outlined"
          startIcon={describing ? <CircularProgress size={16} /> : <PlayArrow />}
          onClick={describeStale}
          disabled={!isAdmin || describing}
        >
          Describe stale months
        </Button>
      </span>
    </Tooltip>
  );

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {header(
        <>
          {doc.coverage.first_month ?? "—"} → {doc.coverage.last_month ?? "—"} ·{" "}
          {fmt(doc.months_covered)} months · generated{" "}
          {formatTs(Date.parse(doc.generated_at) || null)}
        </>,
        <>
          <Button variant="outlined" startIcon={<Download />} onClick={() => renderArchiveReport(doc)}>
            Download PDF
          </Button>
          {describeAction}
        </>,
      )}
      {alerts}
      <PerCorpusSections
        doc={doc}
        contentType={contentType}
        rollupMonths={rollupMonths}
        isAdmin={isAdmin}
        navigate={navigate}
      />
    </Container>
  );
}

// ---- per-corpus body (broadcast + podcast) --------------------------------

function PerCorpusSections({
  doc,
  contentType,
  rollupMonths,
  isAdmin,
  navigate,
}: {
  doc: PerCorpusDoc;
  contentType: string;
  rollupMonths: number | null;
  isAdmin: boolean;
  navigate: NavigateFunction;
}) {
  const { totals } = doc;
  const summary = doc.auto_remarks_summary ?? EMPTY_SUMMARY;
  const isPodcast = contentType === "podcast";
  const incomplete = rollupMonths !== null && doc.months_covered < rollupMonths;

  // The full lists live in the paged /archive/remarks endpoint now. The year
  // table needs every arrival/departure to tally per-year counts (bounded for
  // broadcast; empty for podcast, whose lifecycle is suppressed) — fetch those
  // up front. The station-activity lists below page on demand.
  const [arrivals, setArrivals] = useState<StationRemark[]>([]);
  const [departures, setDepartures] = useState<StationRemark[]>([]);
  useEffect(() => {
    let cancelled = false;
    setArrivals([]);
    setDepartures([]);
    Promise.all([
      getAllArchiveRemarks(contentType, "arrivals"),
      getAllArchiveRemarks(contentType, "departures"),
    ])
      .then(([a, d]) => {
        if (cancelled) return;
        setArrivals(a as StationRemark[]);
        setDepartures(d as StationRemark[]);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [contentType]);

  return (
    <>
      {incomplete && (
        <Alert severity="warning" icon={<Warning />} sx={{ mb: 2 }}>
          Only {fmt(doc.months_covered)} of {fmt(rollupMonths)} indexed {CT_LABEL[contentType]}{" "}
          months have been described. Totals and remarks are <strong>preliminary</strong> until
          the rest are described.
          {isAdmin && " Use “Describe stale months” to complete it."}
        </Alert>
      )}

      {/* Top-line metrics — corpus-specific labels. */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
          gap: 2,
          mb: 3,
        }}
      >
        {isPodcast ? (
          <>
            <MetricBox label="Episodes" value={metric(totals.pages)} />
            <MetricBox label="Hours" value={metric(totals.duration_hours)} />
            <MetricBox label="Shows" value={metric(totals.distinct_stations)} />
          </>
        ) : (
          <>
            <MetricBox label="Pages" value={metric(totals.pages)} />
            <MetricBox label="Programs" value={metric(totals.superdocuments)} />
            <MetricBox label="Hours" value={metric(totals.duration_hours)} />
            <MetricBox label="Sources" value={metric(totals.distinct_stations)} />
          </>
        )}
      </Box>

      <Paper variant="outlined" sx={{ p: 2, mb: 4 }}>
        <Typography variant="overline" color="text.secondary">
          Index coverage over time
        </Typography>
        {/* Page-feed eras are broadcast-only history — omit them on podcast. */}
        <TimelineBarChart
          data={doc.timeline}
          bands={PRESIDENTIAL_TERMS}
          events={WORLD_EVENTS}
          eras={isPodcast ? undefined : DATASET_ERAS}
        />
      </Paper>

      {/* Podcast-only breakdowns. */}
      {isPodcast && (doc.by_language?.length || doc.by_explicit) ? (
        <>
          <SectionTitle>Podcast breakdowns</SectionTitle>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 3 }}>
            {doc.by_language?.length ? (
              <Paper variant="outlined" sx={{ p: 2, flex: 2, minWidth: 0 }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  By language
                </Typography>
                <Box sx={{ maxHeight: 260, overflowY: "auto" }}>
                  <Stack spacing={0.5}>
                    {doc.by_language.map((l) => (
                      <Stack
                        key={l.language ?? "unknown"}
                        direction="row"
                        justifyContent="space-between"
                      >
                        <Typography variant="body2">{l.language ?? "unknown"}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {fmt(l.documents)} episodes
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Box>
              </Paper>
            ) : null}
            {doc.by_explicit ? (
              <Paper variant="outlined" sx={{ p: 2, flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Explicit
                </Typography>
                <Stack spacing={0.5}>
                  {Object.entries(doc.by_explicit).map(([flag, n]) => (
                    <Stack key={flag} direction="row" justifyContent="space-between">
                      <Typography variant="body2">{flag}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {fmt(n)}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Paper>
            ) : null}
          </Stack>
        </>
      ) : null}

      <SectionTitle>Coverage by year · click a year to drill in</SectionTitle>
      <Paper variant="outlined" sx={{ mb: 3, overflowX: "auto" }}>
        <YearCoverageTable
          data={doc.timeline}
          arrivals={arrivals}
          departures={departures}
          onYearClick={(year) => navigate(`/admin/description/${year}?content_type=${contentType}`)}
        />
      </Paper>

      {SHOW_STATION_ACTIVITY && (
        <>
          <SectionTitle>Station activity (auto-detected)</SectionTitle>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 3 }}>
            <PagedRemarkList
              title="Arrivals"
              icon={<Login fontSize="small" color="success" />}
              contentType={contentType}
              category="arrivals"
              total={summary.arrivals}
            />
            <PagedRemarkList
              title="Departures"
              icon={<Logout fontSize="small" color="error" />}
              contentType={contentType}
              category="departures"
              total={summary.departures}
            />
            <PagedRemarkList
              title="Re-keyed"
              icon={<SwapHoriz fontSize="small" color="action" />}
              contentType={contentType}
              category="rekeys"
              total={summary.rekeys}
            />
          </Stack>
          <Stack sx={{ mb: 3 }}>
            <PagedRemarkList
              title="Outages"
              icon={<Warning fontSize="small" color="warning" />}
              contentType={contentType}
              category="outages"
              total={summary.outages}
            />
          </Stack>
        </>
      )}

      <SectionTitle>Errata</SectionTitle>
      <Paper variant="outlined" sx={{ p: 2 }}>
        {doc.errata.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No errata recorded.
          </Typography>
        ) : (
          <Stack spacing={1} divider={<Divider flexItem />}>
            {doc.errata.map((e) => (
              <Box key={`${e.year}-${e.month}`}>
                <Typography variant="subtitle2">
                  {e.year}-{e.month}
                </Typography>
                {e.notes.map((note, i) => (
                  <Typography key={i} variant="body2" color="text.secondary">
                    • {note.text}
                    {note.author ? ` — ${note.author}` : ""}
                  </Typography>
                ))}
              </Box>
            ))}
          </Stack>
        )}
      </Paper>
    </>
  );
}

// ---- cross-set body -------------------------------------------------------

function CrossSetSections({ doc }: { doc: CrossSetDescription }) {
  const theme = useTheme();
  const CORPUS_META: Record<string, { label: string; color: string }> = {
    broadcast: { label: "Broadcast", color: theme.palette.primary.main },
    podcast: { label: "Podcast", color: theme.palette.secondary.main },
  };
  const series: ChartSeries[] = doc.corpora
    .filter((c) => CORPUS_META[c])
    .map((c) => ({ key: c, label: CORPUS_META[c].label, color: CORPUS_META[c].color }));
  const stackedData = (doc.timeline ?? []).map((row) => ({
    month: row.month,
    values: { broadcast: row.broadcast ?? 0, podcast: row.podcast ?? 0 },
  }));
  return (
    <>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
          gap: 2,
          mb: 3,
        }}
      >
        <MetricBox label="Documents" value={metric(doc.totals.documents)} />
        <MetricBox label="Programs" value={metric(doc.totals.superdocuments)} />
        <MetricBox label="Sources" value={metric(doc.totals.distinct_sources)} />
        <MetricBox label="Corpora" value={String(doc.corpora.length)} />
      </Box>

      {stackedData.length > 0 && series.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="overline" color="text.secondary">
            Documents over time — broadcast &amp; podcast
          </Typography>
          <TimelineBarChart
            stackedData={stackedData}
            stacked={series}
            bands={PRESIDENTIAL_TERMS}
            events={WORLD_EVENTS}
            eras={DATASET_ERAS}
          />
        </Paper>
      )}

      <SectionTitle>By content type</SectionTitle>
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Stack spacing={1} divider={<Divider flexItem />}>
          {doc.by_source_type.map((t) => (
            <Stack key={t.source_type} direction="row" alignItems="center" gap={2}>
              <Chip
                size="small"
                icon={t.source_type === "omcs-podcast" ? <Podcasts /> : <Tv />}
                label={t.source_type}
              />
              <Box flexGrow={1} />
              <Typography variant="body2">{fmt(t.documents)} documents</Typography>
              <Typography variant="body2" color="text.secondary">
                {metric(t.duration_hours)} hrs
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Paper>

      <SectionTitle>
        Top sources across all corpora{doc.by_source_truncated ? " (truncated)" : ""}
      </SectionTitle>
      <Paper variant="outlined" sx={{ mb: 3, p: 2 }}>
        <Box sx={{ maxHeight: 320, overflowY: "auto" }}>
          <Stack spacing={0.5}>
            {doc.by_source.slice(0, MAX_LIST_ROWS).map((s) => (
              <Stack key={s.source_id} direction="row" alignItems="center" gap={1}>
                <Chip
                  size="small"
                  variant="outlined"
                  label={s.source_type === "omcs-podcast" ? "podcast" : "broadcast"}
                  sx={{ height: 18, fontSize: "0.65rem" }}
                />
                <Typography variant="body2" noWrap sx={{ flexGrow: 1, minWidth: 0 }}>
                  {s.source_name ?? s.source_id}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {fmt(s.documents)}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Box>
        <MoreRows total={doc.by_source.length} shown={MAX_LIST_ROWS} noun="sources" />
      </Paper>

      <SectionTitle>Top categories (all corpora)</SectionTitle>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Box sx={{ maxHeight: 320, overflowY: "auto" }}>
          <Stack spacing={0.5}>
            {doc.by_category.slice(0, MAX_LIST_ROWS).map((c) => (
              <Stack key={c.category ?? "uncategorized"} direction="row" justifyContent="space-between">
                <Typography variant="body2">{c.category ?? "uncategorized"}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {fmt(c.documents)}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Box>
        <MoreRows total={doc.by_category.length} shown={MAX_LIST_ROWS} noun="categories" />
      </Paper>
    </>
  );
}

function MoreRows({ total, shown, noun }: { total: number; shown: number; noun: string }) {
  if (total <= shown) return null;
  return (
    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
      Showing top {shown} of {fmt(total)} {noun}.
    </Typography>
  );
}

function MetricBox({ label, value }: { label: string; value: string }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, textAlign: "center" }}>
      <Typography variant="h4" sx={{ fontWeight: 600, lineHeight: 1.1 }}>
        {value}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        {label}
      </Typography>
    </Paper>
  );
}

function SectionTitle({ children }: { children: ReactNode }) {
  return (
    <Typography variant="h6" sx={{ mb: 1 }}>
      {children}
    </Typography>
  );
}

// Lazily-paged list of one remark category. Fetches the first page when the
// corpus/category changes (only if the category has any rows, per `total`),
// then "Load more" appends subsequent pages. The full set never loads at once —
// that's what kept the archive doc small.
function PagedRemarkList({
  title,
  icon,
  contentType,
  category,
  total,
}: {
  title: string;
  icon: ReactNode;
  contentType: string;
  category: RemarkCategory;
  total: number;
}) {
  const [items, setItems] = useState<Remark[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setItems([]);
    setErr(null);
    if (total > 0) {
      setLoading(true);
      getArchiveRemarks(contentType, category, 0, REMARK_PAGE)
        .then((r) => !cancelled && setItems(r.remarks))
        .catch((e) => !cancelled && setErr(extractErrorMessage(e)))
        .finally(() => !cancelled && setLoading(false));
    }
    return () => {
      cancelled = true;
    };
  }, [contentType, category, total]);

  const loadMore = () => {
    setLoading(true);
    setErr(null);
    getArchiveRemarks(contentType, category, items.length, REMARK_PAGE)
      .then((r) => setItems((prev) => [...prev, ...r.remarks]))
      .catch((e) => setErr(extractErrorMessage(e)))
      .finally(() => setLoading(false));
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, flex: 1, minWidth: 0 }}>
      <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1 }}>
        {icon}
        <Typography variant="subtitle1">
          {title} ({total.toLocaleString()})
        </Typography>
      </Stack>
      {total === 0 ? (
        <Typography variant="body2" color="text.secondary">
          None detected.
        </Typography>
      ) : (
        <>
          <Box sx={{ maxHeight: 280, overflowY: "auto" }}>
            <Stack spacing={0.5}>
              {items.map((r, i) => (
                <Typography key={`${r.source_id ?? ""}-${i}`} variant="body2">
                  {r.text}
                </Typography>
              ))}
            </Stack>
          </Box>
          <Stack direction="row" alignItems="center" gap={1} sx={{ mt: 1 }}>
            {items.length < total && (
              <Button size="small" onClick={loadMore} disabled={loading}>
                Load more ({items.length.toLocaleString()} / {total.toLocaleString()})
              </Button>
            )}
            {loading && <CircularProgress size={16} />}
          </Stack>
          {err && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {err}
            </Alert>
          )}
        </>
      )}
    </Paper>
  );
}
